import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '../../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinDataSender } from '../../../common/data-retrieval/odin-data-sender';
import useTestTag from '../../../common/hooks/use-test-tag';
import type { SearchValueType, TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import { DataSearchLocation } from '../../enums';
import type { ActionSectionItem } from '../../fields/drop-down/drop-down-action-section.component';
import { isDropDownNoSelection } from '../../fields/drop-down/drop-down-common';
import type { DropDownFeatures } from '../../fields/drop-down/drop-down-features';
import type {
  DropDownNoSelection,
  DropDownResult,
  DropDownSelectorTemplateProps,
} from '../../fields/drop-down/drop-down-types';
import { DropDownSelectionType } from '../../fields/drop-down/drop-down-types';
import DropDown from '../../fields/drop-down/drop-down.component';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import type { DataGridFilterProps } from './data-grid-filter.interface';
import { OverlayScrollDetectionType } from '../../common/overlay-panel/overlay-panel.props';
import type { Signal } from '@preact/signals-react';

interface DataGridDropDownFilterProps extends DataGridFilterProps<Array<DropDownResult>> {
  columns: Signal<Array<DataGridColumnSettings>>;
}

export default function DataGridDropDownFilter({
  columns,
  search,
  column,
  filterSignal,
  scrollDetection = OverlayScrollDetectionType.Parent,
  forceResetColumnMap,
}: DataGridDropDownFilterProps) {
  const { t } = useTranslation();

  const [currentValue, setCurrentValue] = useState<Array<DropDownResult>>([]);

  const previousColumn = useRef<DataGridColumnSettings | undefined>(undefined);

  const tagCreator = useTestTag(column.title || '');

  const blankFilter: DropDownResult = useMemo(
    () => ({
      value: 'blank',
      text: t('blank') ?? 'Blank',
    }),
    [t]
  );

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      const currentSearchValue = Array.isArray(column.searchValue) ? (column.searchValue as Array<DropDownResult>) : [];
      const filteredSearchValue = currentSearchValue.filter((value) => value.value !== blankFilter.value);

      updateFilters(currentSearchValue);
      setCurrentValue(column.showBlankAction ? filteredSearchValue : currentSearchValue);
    }

    previousColumn.current = column;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger on column change, updateFilters and other deps are stable
  }, [column]);

  const updateFilters = (value: Array<DropDownResult>) => {
    const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
    const clonedFilters = cloneDeep(filterSignal.value);
    if (filterIndex > -1) {
      clonedFilters.splice(filterIndex, 1, {
        scrollerId: column.id,
        field: column.field,
        customDataProperties: column.customDataProperties,
        comparison: column.searchComparison ?? ContinuousScrollerComparisonType.Contains,
        value: value.flatMap((item) => item.value) as Array<SearchValueType>,
        text: value.map((item) => item.text),
      });
      filterSignal.value = clonedFilters;
    } else {
      clonedFilters.push({
        scrollerId: column.id,
        field: column.field,
        customDataProperties: column.customDataProperties,
        comparison: column.searchComparison ?? ContinuousScrollerComparisonType.Contains,
        value: value.flatMap((item) => item.value) as Array<SearchValueType>,
        text: value.map((item) => item.text),
      });
      filterSignal.value = clonedFilters;
    }
  };

  const removeFilter = () => {
    if (filterSignal.value.findIndex((filter) => filter.scrollerId === column.id) > -1) {
      filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
    }
  };

  const getFilterValues = (value: Array<DropDownResult>) => {
    const selectedValues = cloneDeep(value);
    const isBlankFilterIncluded = filterSignal.value
      .filter((filter) => filter.scrollerId === column.id)
      .some((filter) => (filter.value as Array<SearchValueType>).includes(String(blankFilter.value)));

    if (isBlankFilterIncluded) {
      selectedValues.push(blankFilter);
    }

    return selectedValues;
  };

  const onSelectionChange = (value?: TypeOrArray<DropDownResult> | DropDownNoSelection) => {
    if (value && !isDropDownNoSelection(value) && Array.isArray(value) && value.length > 0) {
      const filterValues = getFilterValues(value);

      updateFilters(filterValues);
      setCurrentValue(value);
    } else {
      const filterValues = getFilterValues([]);
      if (filterValues.length > 0) {
        updateFilters(filterValues);
      } else {
        removeFilter();
      }

      setCurrentValue([]);
    }
  };

  if (!column.dropDownText || !column.dropDownValue) {
    throw new Error('The dropDownText and dropDownValue properties must be set to use the drop down filter');
  }

  const dropDownData = useCallback(
    (scrollerSubscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (search && !Array.isArray(search)) {
        const scrollerOptions: OdinDataRetrievalOptions = {
          ...options,
          customProperties: { ...column.customDataProperties, ...options?.customProperties },
        };

        search.getData(scrollerSubscriber, scrollerOptions);
      }
    },
    [search, column.customDataProperties]
  );

  const dataRetriever: OdinDataRetrieval = useMemo(() => {
    if (search && !Array.isArray(search) && search.getSubscriber) {
      return {
        getData: dropDownData,
        getSubscriber: search.getSubscriber,
      };
    }

    return {
      getData: dropDownData,
    };
  }, [search, dropDownData]);

  const actionItems = useMemo<Array<ActionSectionItem> | undefined>(() => {
    const actionItems: Array<ActionSectionItem> = [];

    if (column?.showBlankAction === true) {
      const currentSearchValue = Array.isArray(column.searchValue) ? (column.searchValue as Array<DropDownResult>) : [];
      const isBlankValueSelected = currentSearchValue.some((value) => value.value === blankFilter.value);
      actionItems.push({ ...blankFilter, selected: isBlankValueSelected });
    }

    if (column?.showArchivedAction === true) {
      actionItems.push({
        value: 'archived',
        text: t('show-archived-values') ?? 'Show Archived Values',
        selected: Boolean(column.includeArchived),
      });
    }

    return actionItems.length > 0 ? actionItems : undefined;
  }, [blankFilter, column?.showArchivedAction, column?.showBlankAction, column.includeArchived, column.searchValue, t]);

  const handleActionItemChanged = (item: ActionSectionItem, features: DropDownFeatures) => {
    if (item.value === 'archived') {
      const filter: ContinuousScrollerFilter = {
        scrollerId: column.id,
        field: String(item.value),
        value: Boolean(item.selected),
        comparison: ContinuousScrollerComparisonType.Equals,
      };

      features.addFilter(filter);

      const changedColumn: DataGridColumnSettings = cloneDeep(column);
      changedColumn.includeArchived = Boolean(item.selected);

      for (let i = 0, length = columns.value.length; i < length; i++) {
        if (columns.value[i].id === changedColumn.id) {
          columns.value[i] = changedColumn;
        }
      }
    } else if (item.value === blankFilter.value) {
      if (item.selected) {
        updateFilters([...currentValue, ...[blankFilter]]);
      } else {
        if (currentValue.length > 0) {
          updateFilters([...currentValue]);
        } else {
          removeFilter();
        }
      }
    }
  };

  const selectorTemplate = ({ selectedValue, multipleMessageCount }: DropDownSelectorTemplateProps) => {
    const selectedValues = cloneDeep(selectedValue ?? []);
    const isBlankFilterIncluded = filterSignal.value
      .filter((filter) => filter.scrollerId === column.id)
      .some((filter) => (filter.value as Array<SearchValueType>).includes(String(blankFilter.value)));

    if (isBlankFilterIncluded) {
      selectedValues.push(blankFilter);
    }

    if (selectedValues.length >= multipleMessageCount) {
      return <>{t('multiple', 'Multiple')}</>;
    }

    //multipleMessageCount is 2, so the array only has one item or none here
    return <>{selectedValues[0]?.text}</>;
  };

  return (
    <div className="odin-w-full" ref={tagCreator('drop-down-filter')}>
      <DropDown
        id={column.id}
        textField={column.dropDownText}
        valueField={column.dropDownValue}
        archivedField={column.dropDownArchived}
        value={currentValue}
        selectionType={DropDownSelectionType.Multiple}
        data={column.dropDownData ?? dataRetriever}
        multipleMessageCount={2}
        useChips={false}
        allowClear={false}
        allowSearch={true}
        showArchivedOptions={column.includeArchived}
        scrollDetection={scrollDetection}
        onChange={onSelectionChange}
        filterLocation={DataSearchLocation.Api}
        shouldUseCreatePortal={true}
        actionItems={actionItems}
        onActionItemChanged={handleActionItemChanged}
        selectorTemplate={selectorTemplate}
      />
    </div>
  );
}
